// === Certification Exam Types ===
// Based on Unity Certified User: Programmer specification

/**
 * Question Type Enum - All supported question layouts
 */
export enum CertQuestionType {
    TYPE_MCQ_SINGLE = 'TYPE_MCQ_SINGLE',
    TYPE_MCQ_MULTI = 'TYPE_MCQ_MULTI',
    TYPE_MATRIX = 'TYPE_MATRIX',
    TYPE_MATCHING_DRAG = 'TYPE_MATCHING_DRAG',
    TYPE_CODE_CONSTRUCTION = 'TYPE_CODE_CONSTRUCTION',
    TYPE_CODE_DROPDOWN = 'TYPE_CODE_DROPDOWN',
    TYPE_CODE_INPUT = 'TYPE_CODE_INPUT',
    TYPE_BOOLEAN = 'TYPE_BOOLEAN',
    TYPE_ORDERING = 'TYPE_ORDERING',
    TYPE_HOTSPOT = 'TYPE_HOTSPOT',
    TYPE_IMAGE_DROPDOWN = 'TYPE_IMAGE_DROPDOWN'
}

/**
 * Content types for rich question elements
 */
export type CertContentType = 'text' | 'code';

/**
 * Base question interface with common fields
 */
export interface CertQuestionBase {
    id: string;
    type: CertQuestionType;
    tags: string[];
    prompt: string;
    image_url?: string | null;
    partial_credit?: boolean;
    /** Explication montrée seulement à la correction (jamais pendant l'examen : elle donnerait la réponse). */
    explanation?: string;
}

// === MCQ Single ===
export interface CertOptionItem {
    id: string;
    content_type: CertContentType;
    value: string;
}

export interface CertQuestionMCQSingle extends CertQuestionBase {
    type: CertQuestionType.TYPE_MCQ_SINGLE;
    content: {
        context_code?: string;
        options: CertOptionItem[];
    };
    validation: {
        correct_answer: string;
    };
}

// === MCQ Multi ===
export interface CertQuestionMCQMulti extends CertQuestionBase {
    type: CertQuestionType.TYPE_MCQ_MULTI;
    content: {
        context_code?: string;
        options: CertOptionItem[];
        max_selection: number;
    };
    validation: {
        correct_ids: string[];
    };
}

// === Matrix (Yes/No Grid) ===
export interface CertMatrixRow {
    id: string;
    content_type: CertContentType;
    value: string;
}

export interface CertQuestionMatrix extends CertQuestionBase {
    type: CertQuestionType.TYPE_MATRIX;
    content: {
        context_code?: string;
        columns: string[];
        rows: CertMatrixRow[];
    };
    validation: Record<string, number>; // row_id -> column index
}

// === Matching Drag ===
export interface CertSourceItem {
    id: string;
    content_type: CertContentType;
    value: string;
}

export interface CertTargetItem {
    id: string;
    content_type: CertContentType;
    value: string;
}

export interface CertQuestionMatchingDrag extends CertQuestionBase {
    type: CertQuestionType.TYPE_MATCHING_DRAG;
    content: {
        context_code?: string;
        sources: CertSourceItem[];
        targets: CertTargetItem[];
    };
    validation: Record<string, string>; // target_id -> source_id
}

// === Ordering ===
export interface CertOrderingItem {
    id: string;
    content_type: CertContentType;
    value: string;
}

export interface CertQuestionOrdering extends CertQuestionBase {
    type: CertQuestionType.TYPE_ORDERING;
    content: {
        context_code?: string;
        items: CertOrderingItem[];
    };
    validation: {
        correct_order: string[];
    };
}

// === Code Dropdown ===
export interface CertDropdownOption {
    placeholder: string;
    options: string[];
}

export interface CertQuestionCodeDropdown extends CertQuestionBase {
    type: CertQuestionType.TYPE_CODE_DROPDOWN;
    content: {
        context_code?: string;
        reference_code?: string;
        code_template: string;
        dropdowns: Record<string, CertDropdownOption>;
    };
    validation: Record<string, string>; // dropdown_id -> correct value
}

// === Code Input ===
export interface CertInputField {
    placeholder: string;
    expected_width?: 'small' | 'medium' | 'large';
}

export interface CertQuestionCodeInput extends CertQuestionBase {
    type: CertQuestionType.TYPE_CODE_INPUT;
    content: {
        context_code?: string;
        code_template: string;
        inputs: Record<string, CertInputField>;
    };
    // input_id -> bonne réponse, ou liste des réponses acceptées (la première est celle affichée)
    validation: Record<string, string | string[]>;
}

// === Boolean ===
export interface CertQuestionBoolean extends CertQuestionBase {
    type: CertQuestionType.TYPE_BOOLEAN;
    content: {
        context_code?: string;
        content_block: string;
    };
    validation: {
        correct_value: boolean;
    };
}

// === Code Construction (Drag to Slots in Fixed Template) ===
export interface CertBankItem {
    id: string;
    content_type: CertContentType;
    value: string;
}

export interface CertQuestionCodeConstruction extends CertQuestionBase {
    type: CertQuestionType.TYPE_CODE_CONSTRUCTION;
    content: {
        context_code?: string;
        bank: CertBankItem[];
        code_template: string; // Contains [[slot_id]] placeholders
    };
    // slot_id -> bank_item_id, ou liste des éléments acceptés (ordre libre ; le premier est celui affiché)
    validation: Record<string, string | string[]>;
}

// === Hotspot (Image Click) ===
export interface CertHotspot {
    id: string;
    rect: [number, number, number, number]; // x, y, width, height
    name: string;
}

export interface CertQuestionHotspot extends CertQuestionBase {
    type: CertQuestionType.TYPE_HOTSPOT;
    content: {
        image_url: string;
        hotspots: CertHotspot[];
    };
    validation: {
        correct_hotspot: string;
    };
}

// === Image Dropdown ===
export interface CertQuestionImageDropdown extends CertQuestionBase {
    type: CertQuestionType.TYPE_IMAGE_DROPDOWN;
    content: {
        image_url: string;
        text_template: string;
        dropdowns: Record<string, CertDropdownOption & { correct_index: number }>;
    };
    validation: Record<string, number>; // dropdown_id -> correct option index
}

// === Union type for all question types ===
export type CertQuestion =
    | CertQuestionMCQSingle
    | CertQuestionMCQMulti
    | CertQuestionMatrix
    | CertQuestionMatchingDrag
    | CertQuestionOrdering
    | CertQuestionCodeDropdown
    | CertQuestionCodeInput
    | CertQuestionBoolean
    | CertQuestionCodeConstruction
    | CertQuestionHotspot
    | CertQuestionImageDropdown;

// === Exam Definition ===
export interface CertExamDefinition {
    exam_id: string;
    title: string;
    duration_minutes: number;
    passing_score_percent: number;
    points_per_question: number;
    unity_version: string;
    questions: CertQuestion[];
}

// === User Answer Types ===
export type CertAnswer =
    | string // MCQ Single
    | string[] // MCQ Multi, Ordering
    | Record<string, string> // Matching, Dropdowns, Inputs, Code Construction
    | Record<string, number> // Matrix column index
    | boolean // Boolean True/False
    | { x: number; y: number }; // Hotspot

export interface CertQuestionAnswer {
    questionId: string;
    /** `null` tant qu'une question vrai/faux n'a pas été répondue (pas de valeur neutre possible). */
    response: CertAnswer | null;
    isFlagged: boolean;
    isAnswered: boolean;
}

// === Exam Attempt ===
export interface CertExamAttempt {
    id: string;
    examId: string;
    userId: string;
    startedAt: Date;
    finishedAt?: Date;
    submissionType: 'manual' | 'timeout';
    score: number;
    maxScore: number;
    passed: boolean;
    answers: Record<string, CertQuestionAnswer>;
}

// === Scoring Result ===
export interface CertQuestionResult {
    questionId: string;
    isCorrect: boolean;
    pointsEarned: number;
    userAnswer: CertAnswer;
    correctAnswer: CertAnswer;
    /** Saisie et construction de code : toutes les réponses acceptées par case, pour colorer la copie comme la notation. */
    acceptedAnswers?: Record<string, string[]>;
    /** Copiée de la question au moment de servir la correction, jamais stockée avec la copie. */
    explanation?: string;
}

export interface CertExamResult {
    attemptId: string;
    score: number;
    maxScore: number;
    passed: boolean;
    passingThreshold: number;
    questionResults: CertQuestionResult[];
}
